import React, { useMemo, useState, useEffect, useRef } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import useThemeColor from "../../hooks/useThemeColor.js";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import "./resturantCategoryModal.css";

const getApiBaseUrl = () => {
  const raw =
    process.env.REACT_APP_Environment === "development"
      ? process.env.REACT_APP_API_URL
      : process.env.REACT_APP_API_URL_Production;
  return (raw || "").replace(/\/$/, "");
};

/** Normalize to an absolute https URL when possible (upload/save expects absolute). */
const toAbsoluteImageUrl = (value, apiBaseUrl) => {
  if (!value || !String(value).trim()) return "";
  let path = String(value).trim().replace(/\\/g, "/");
  if (/^https?:\/\//i.test(path)) return path;
  const base = (apiBaseUrl || "").replace(/\/$/, "");
  if (!base) return path.replace(/^\//, "");
  return `${base}/${path.replace(/^\//, "")}`;
};

const AddUpdateResturantCategoryModal = ({ show, onHide, onSubmit, isEdit, category }) => {
  const themeColor = useThemeColor();
  const fileInputRef = useRef(null);
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    isActive: true,
    image: "",
  });
  const [errors, setErrors] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewBroken, setPreviewBroken] = useState(false);

  useEffect(() => {
    if (isEdit && category) {
      setFormData({
        name: category.name || "",
        slug: category.slug || "",
        isActive: category.isActive ?? true,
        image: toAbsoluteImageUrl(category.image || "", apiBaseUrl),
      });
    } else {
      setFormData({ name: "", slug: "", isActive: true, image: "" });
    }
    setErrors({});
    setPreviewBroken(false);
  }, [isEdit, category, show, apiBaseUrl]);

  const previewSrc = useMemo(
    () => toAbsoluteImageUrl(formData.image, apiBaseUrl),
    [formData.image, apiBaseUrl]
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
    if (name === "image") setPreviewBroken(false);
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.slug.trim()) newErrors.slug = "Slug is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    onSubmit(
      {
        id: category?.id || 0,
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        // Order is managed by drag-and-drop on the list. Keep existing on edit; insert SP assigns next when 0.
        sortOrder: isEdit ? category?.sortOrder || 0 : 0,
        isActive: formData.isActive,
        image: toAbsoluteImageUrl(formData.image, apiBaseUrl) || null,
      },
      isEdit
    );
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      showErrorToast("Please select a valid image file (jpg, png, webp, gif).");
      return;
    }

    const uploadForm = new FormData();
    uploadForm.append("ImageFile", file);

    try {
      setUploadingImage(true);
      const response = await axiosInstance.post(APIs.POST_UPLOAD_RESTURANT_CATEGORY_IMAGE, uploadForm, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data?.status) {
        const uploadedPath =
          response.data?.data?.filePath ||
          response.data?.data?.FilePath ||
          response.data?.data?.relativePath ||
          "";
        const absolute = toAbsoluteImageUrl(uploadedPath, apiBaseUrl);
        setFormData((prev) => ({ ...prev, image: absolute }));
        setPreviewBroken(false);
        showSuccessToast("Image uploaded successfully.");
      } else {
        showErrorToast(response.data?.message || "Failed to upload image.");
      }
    } catch {
      showErrorToast("Failed to upload image.");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, image: "" }));
    setPreviewBroken(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" dialogClassName="resturant-category-modal" backdrop="static" keyboard={false}>
      <Modal.Header closeButton className="rcm-header">
        <Modal.Title className="rcm-title">{isEdit ? "Update Restaurant Category" : "Add Restaurant Category"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="rcm-body">
        <Form onSubmit={handleSubmit}>
          <div className="rcm-section">
            <h3 className="rcm-section-title">Details</h3>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="rcm-label">Name</Form.Label>
                  <Form.Control
                    className="rcm-input"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g. Biryani"
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="rcm-label">Slug</Form.Label>
                  <Form.Control
                    className="rcm-input"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    placeholder="e.g. biryani"
                    isInvalid={!!errors.slug}
                  />
                  <Form.Control.Feedback type="invalid">{errors.slug}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={12}>
                <div className="rcm-status-row">
                  <div>
                    <div className="rcm-label mb-0">Status</div>
                    <div className="rcm-help-text mb-0">Inactive categories stay hidden on Go Fatafat.</div>
                  </div>
                  <Form.Check
                    type="switch"
                    id="isActive"
                    name="isActive"
                    label={formData.isActive ? "Active" : "Inactive"}
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>
            </Row>
          </div>

          <div className="rcm-section">
            <h3 className="rcm-section-title">Category image</h3>
            <div className="rcm-image-panel">
              <div className="rcm-image-preview-wrap">
                {previewSrc && !previewBroken ? (
                  <img
                    src={previewSrc}
                    alt="Category preview"
                    className="rcm-preview-image"
                    onError={() => setPreviewBroken(true)}
                  />
                ) : (
                  <div className="rcm-image-placeholder">
                    <i className="fa fa-image" />
                    <span>{previewBroken ? "Preview unavailable" : "No image"}</span>
                  </div>
                )}
              </div>

              <div className="rcm-image-controls">
                <Form.Group className="mb-3">
                  <Form.Label className="rcm-label">Image URL</Form.Label>
                  <Form.Control
                    className="rcm-input"
                    name="image"
                    value={formData.image}
                    onChange={handleInputChange}
                    placeholder="https://…"
                  />
                  <Form.Text className="rcm-help-text">
                    Uploads are saved as a full absolute URL.
                  </Form.Text>
                </Form.Group>

                <div className="rcm-image-actions">
                  <label className={`rcm-upload-btn ${uploadingImage ? "is-disabled" : ""}`} style={{ "--rcm-theme": themeColor }}>
                    <i className={`fa ${uploadingImage ? "fa-spinner fa-spin" : "fa-cloud-upload-alt"}`} />
                    {uploadingImage ? "Uploading…" : "Upload image"}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      hidden
                    />
                  </label>
                  {formData.image ? (
                    <button type="button" className="rcm-remove-btn" onClick={handleRemoveImage}>
                      <i className="fa fa-trash" />
                      Remove
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer className="rcm-footer">
        <Button variant="secondary" className="rcm-cancel-btn" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          className="rcm-submit-btn"
          onClick={handleSubmit}
          style={{ backgroundColor: themeColor, borderColor: themeColor }}
        >
          {isEdit ? "Update" : "Create"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddUpdateResturantCategoryModal;
